<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";

const props = defineProps({
    settings: {
        type: Object,
        default: () => ({}),
    },
});

// Settings
const stateDataJson = computed(() => props.settings.state_data || "{}");
const mapColor = computed(() => props.settings.map_color || "#0C3A51");
const hoverColor = computed(() => props.settings.hover_color || "#00A1CF");

// Parse state data
const stateData = computed(() => {
    try {
        return JSON.parse(stateDataJson.value);
    } catch (e) {
        return {
            NV: { name: "Nevada", agents: 42, teams: 10 },
            CA: { name: "California", agents: 156, teams: 23 },
            TX: { name: "Texas", agents: 89, teams: 15 },
            NY: { name: "New York", agents: 134, teams: 28 },
            FL: { name: "Florida", agents: 98, teams: 19 },
        };
    }
});

// State management
const selectedState = ref(null);
const selectedStatePath = ref(null);
const hoveredState = ref(null);
const tooltipPosition = ref({ x: 0, y: 0 });
const tooltipAlign = ref("center"); // 'left', 'center', 'right'
const mapContainer = ref(null);

// Cached display data (keeps values during fade-out transition)
const displayState = ref(null);
const displayStatePath = ref(null);
const showTooltip = ref(false);

// Get state info
function getStateInfo(stateCode) {
    return (
        stateData.value[stateCode] || { name: stateCode, agents: 0, teams: 0 }
    );
}

// Calculate tooltip position above cursor
function updateTooltipPosition(event) {
    if (!mapContainer.value) return;

    const containerRect = mapContainer.value.getBoundingClientRect();

    // Cursor position inside container
    const clickX = event.clientX - containerRect.left;
    const clickY = event.clientY - containerRect.top;

    // Tooltip offset (always above + right)
    const offsetX = 10;
    const offsetY = 10;

    // Initial tooltip position
    let x = clickX + offsetX;
    let y = clickY - offsetY; // above cursor

    // Tooltip dimensions (match your CSS min-width + padding)
    const tooltipWidth = 200;
    const tooltipHeight = 120;

    // Clamp horizontally
    if (x + tooltipWidth > containerRect.width) {
        x = containerRect.width - tooltipWidth;
    }
    if (x < 0) x = 0;

    // Clamp vertically
    if (y - tooltipHeight < 0) {
        y = tooltipHeight; // fallback so it stays visible
    }

    tooltipPosition.value = { x, y };
}

// Handle click outside to close tooltip
function handleClickOutside(event) {
    const target = event.target;
    const isStateClick = target.closest("path[data-state]");

    if (!isStateClick && showTooltip.value) {
        hideTooltip();
    }
}

// Show tooltip with state data
function showStateTooltip(stateCode, pathData, event) {
    selectedState.value = stateCode;
    selectedStatePath.value = pathData;
    displayState.value = stateCode;
    displayStatePath.value = pathData;
    updateTooltipPosition(event);
    showTooltip.value = true;
}

// Hide tooltip (keeps display data for fade-out)
function hideTooltip() {
    showTooltip.value = false;
    selectedState.value = null;
    selectedStatePath.value = null;
}

// Clear cached data after transition completes
function onTooltipAfterLeave() {
    displayState.value = null;
    displayStatePath.value = null;
}

// Get path data for tooltip SVG
function getPathData(pathElement) {
    const d = pathElement.getAttribute("d");
    const bbox = pathElement.getBBox();

    return {
        d,
        viewBox: `${bbox.x - 5} ${bbox.y - 5} ${bbox.width + 10} ${bbox.height + 10}`,
    };
}

// Initialize map
async function initializeMap() {
    await nextTick();

    const svgText = await fetch(
        import.meta.env.BASE_URL + "assets/us-map.svg",
    ).then((r) => r.text());
    const mapWrapper = mapContainer.value?.querySelector(".map-wrapper");
    if (!mapWrapper) return;

    mapWrapper.innerHTML = svgText;
    await nextTick();

    const svgElement = mapWrapper.querySelector("svg");
    if (!svgElement) return;

    const statePaths = svgElement.querySelectorAll("path[data-state]");

    statePaths.forEach((path) => {
        const stateCode = path.getAttribute("data-state");
        if (!stateCode) return;

        path.style.cursor = "pointer";
        path.style.transition = "fill 0.2s ease";
        path.setAttribute("fill", mapColor.value);

        // Hover effect for color change
        path.addEventListener("mouseenter", () => {
            path.setAttribute("fill", hoverColor.value);
            hoveredState.value = stateCode;
        });

        path.addEventListener("mouseleave", () => {
            path.setAttribute("fill", mapColor.value);
            hoveredState.value = null;
        });

        // Click to show/toggle tooltip
        path.addEventListener("click", (e) => {
            e.stopPropagation();
            showStateTooltip(stateCode, getPathData(path), e);
        });
    });

    // Add click-outside listener
    document.addEventListener("click", handleClickOutside);
}

onMounted(initializeMap);

onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
});
</script>

<template>
    <div ref="mapContainer" class="us-map-widget relative w-full">
        <!-- Map Container -->
        <div class="map-container relative bg-white">
            <!-- Scrollable wrapper for mobile -->
            <div class="map-scroll-container overflow-x-auto">
                <div class="map-wrapper min-h-[400px] mx-auto">
                    <div
                        class="flex items-center justify-center h-96 text-gray-500"
                    >
                        Loading map...
                    </div>
                </div>
            </div>
        </div>

        <!-- Tooltip on Click -->
        <Transition name="fade" @after-leave="onTooltipAfterLeave">
            <div
                v-if="showTooltip"
                class="tooltip absolute z-50 pointer-events-none"
                :style="{
                    left: tooltipPosition.x + 'px',
                    bottom: 'calc(100% - ' + tooltipPosition.y + 'px)',
                }"
            >
                <div class="tooltip-modal">
                    <!-- Text Content on Left -->
                    <div class="flex-1">
                        <h3 class="tooltip-title">
                            {{ getStateInfo(displayState).name }}
                        </h3>
                        <div class="space-y-1">
                            <div class="tooltip-stat">
                                {{ getStateInfo(displayState).agents }} Agents
                            </div>
                        </div>
                    </div>

                    <!-- State Shape on Right -->
                    <div v-if="displayStatePath" class="flex items-center">
                        <svg
                            :viewBox="displayStatePath.viewBox"
                            class="w-20 h-20"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <path :d="displayStatePath.d" class="state-shape" />
                        </svg>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
.us-map-widget {
    font-family: "Inter", system-ui, sans-serif;
}

.map-scroll-container {
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

.map-wrapper :deep(svg) {
    min-width: 800px; /* Keep map large on mobile */
    width: 100%;
    height: auto;
    display: block;
    margin-left: auto;
    margin-right: auto;
}

/* Desktop: allow map to be responsive */
@media (min-width: 1024px) {
    .map-wrapper :deep(svg) {
        min-width: auto;
    }
}

.tooltip-modal {
    background: white;
    box-shadow:
        0 20px 25px -5px rgb(0 0 0 / 0.1),
        0 8px 10px -6px rgb(0 0 0 / 0.1);
    min-width: 200px;
    display: flex;
    gap: 1rem;
    border-radius: 12px;
    border: 1px solid #e5ecee;
    padding: 24px;
}

.tooltip-title {
    margin-bottom: 0.5rem;
    font-weight: 800;
    font-size: 24px;
    line-height: 32px;
    color: #0c3a51;
}

.tooltip-stat {
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
    color: #0c3a51;
}

.state-shape {
    fill: #a2b7ba4d;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
